import { Box, Chip, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import {
    AgentSkillHistoryItem,
    AgentSkillListItem,
} from "components/AgentSkills/Models/AgentSkill";
import AgentSkillsStore from "components/AgentSkills/Stores/AgentSkillsStore";
import { useStore } from "utils/useStore";

interface Props {
    skill: AgentSkillListItem | null;
    onClose: () => void;
}

const formatDateTime = (value?: string | null) =>
    value ? new Date(value).toLocaleString() : "-";

const AgentSkillHistoryDialog: React.FC<Props> = observer(({ skill, onClose }) => {
    const store = useStore(AgentSkillsStore);
    const [history, setHistory] = useState<AgentSkillHistoryItem[]>([]);
    const taskName = skill ? AgentSkillsStore.Tasks.LoadHistory(skill.id) : "";
    const loading = store.getTaskLoading(taskName);

    useEffect(() => {
        if (!skill) {
            setHistory([]);
            return;
        }

        void store.loadSkillHistory(skill.id).then((versions) => {
            setHistory(versions ?? []);
        });
    }, [skill, store]);

    return (
        <AcxDialog
            isOpen={Boolean(skill)}
            onClose={onClose}
            title={`${skill?.name ?? "Skill"} version history`}
            subTitle="Immutable versions are retained when a skill is changed."
            maxWidth="sm"
            fullWidth
            hideCancelButton
            dialogContentChildren={
                loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={28} />
                    </Box>
                ) : history.length === 0 ? (
                    <Typography color="text.secondary">
                        No version history is available.
                    </Typography>
                ) : (
                    <Stack divider={<Divider flexItem />} spacing={1}>
                        {history.map((version) => (
                            <Stack key={version.id} spacing={0.5} py={1}>
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                    <Chip
                                        size="small"
                                        color={version.isActive ? "success" : "default"}
                                        label={version.isActive ? "Current version" : "Retired version"}
                                    />
                                    {version.isInLibrary && (
                                        <Chip size="small" label="Library origin" variant="outlined" />
                                    )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    Created {formatDateTime(version.createdOn)} by {version.createdBy || "Unknown"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Modified {formatDateTime(version.modifiedOn)} by {version.modifiedBy || "Unknown"}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                )
            }
        />
    );
});

export default AgentSkillHistoryDialog;
